import { FileInfo } from 'interfaces/fileList';
import { size } from 'lodash';

export type SlideIntakeRequestState = 'ready' | 'loading' | 'error' | 'stale';

export type SlideIntakeFileKind = 'slide' | 'manifest' | 'unsupported';

export type SlideIntakeLifecycleState =
  | 'loading'
  | 'requestError'
  | 'unsupportedFile'
  | 'manifestFile'
  | 'hidden'
  | 'needsAttention'
  | 'stale'
  | 'rejectedIngest'
  | 'ingesting'
  | 'ingestFailed'
  | 'queuedForIngest'
  | 'needsCaseAssignment'
  | 'assignedToCase';

export type SlideIntakeLifecycleTone = 'default' | 'info' | 'success' | 'warning' | 'error';

export type SlideIntakeActionCategory =
  | 'none'
  | 'retryLoad'
  | 'openManifest'
  | 'restoreUpload'
  | 'investigate'
  | 'viewIngestJob'
  | 'retryIngest'
  | 'trackIngest'
  | 'organizeSlides'
  | 'openAssignedCase';

export interface SlideIntakeEvidence {
  requestState: SlideIntakeRequestState;
  fileKind: SlideIntakeFileKind;
  isHidden?: boolean;
  hasContradictoryEvidence?: boolean;
  hasRejectedIngest?: boolean;
  hasActiveIngest?: boolean;
  hasFailedIngest?: boolean;
  hasUsableSuccessfulIngest?: boolean;
  hasCaseAssignment?: boolean;
}

export interface SlideIntakeLifecyclePresentation {
  state: SlideIntakeLifecycleState;
  label: string;
  explanation: string;
  tone: SlideIntakeLifecycleTone;
  actionCategory: SlideIntakeActionCategory;
}

export const getFileInfoSlideIntakeEvidence = (
  row: FileInfo,
  requestState: SlideIntakeRequestState = 'ready',
  fileKind: SlideIntakeFileKind = row.isManifest ? 'manifest' : 'slide'
): SlideIntakeEvidence => ({
  requestState,
  fileKind,
  isHidden: row.isHidden,
  hasRejectedIngest: Boolean(row.latestSuccessfulIngestJobArchived),
  hasActiveIngest: Boolean(row.activeIngestJob),
  hasFailedIngest:
    !row.latestSuccessfulIngestJob && !row.slideId && (Boolean(row.latestFailedIngestJob) || row.status === 'failed'),
  hasUsableSuccessfulIngest:
    !row.latestSuccessfulIngestJobArchived &&
    (Boolean(row.latestSuccessfulIngestJob) || Boolean(row.slideId) || row.status === 'completed'),
  hasCaseAssignment: size(row.cases || []) > 0,
});

const lifecyclePresentations: Record<SlideIntakeLifecycleState, SlideIntakeLifecyclePresentation> = {
  loading: {
    state: 'loading',
    label: 'Loading',
    explanation: 'Upload and ingest status is loading.',
    tone: 'default',
    actionCategory: 'none',
  },
  requestError: {
    state: 'requestError',
    label: 'Status unavailable',
    explanation: 'Upload and ingest status could not be loaded.',
    tone: 'error',
    actionCategory: 'retryLoad',
  },
  unsupportedFile: {
    state: 'unsupportedFile',
    label: 'Unsupported file',
    explanation: 'This file is not supported for slide ingest.',
    tone: 'warning',
    actionCategory: 'none',
  },
  manifestFile: {
    state: 'manifestFile',
    label: 'Manifest file',
    explanation: 'This file can be used in the advanced manifest workflow.',
    tone: 'info',
    actionCategory: 'openManifest',
  },
  hidden: {
    state: 'hidden',
    label: 'Hidden',
    explanation: 'This upload is hidden from the active intake workflow.',
    tone: 'default',
    actionCategory: 'restoreUpload',
  },
  needsAttention: {
    state: 'needsAttention',
    label: 'Needs attention',
    explanation: 'The available upload and ingest evidence is contradictory.',
    tone: 'warning',
    actionCategory: 'investigate',
  },
  stale: {
    state: 'stale',
    label: 'Status changed',
    explanation: 'Refresh this upload before taking another action.',
    tone: 'warning',
    actionCategory: 'retryLoad',
  },
  rejectedIngest: {
    state: 'rejectedIngest',
    label: 'Rejected ingest',
    explanation: 'The prior ingest was rejected and is not usable.',
    tone: 'warning',
    actionCategory: 'retryIngest',
  },
  ingesting: {
    state: 'ingesting',
    label: 'Ingesting',
    explanation: 'Ingest is running. The slide is not ready for case assignment yet.',
    tone: 'info',
    actionCategory: 'viewIngestJob',
  },
  ingestFailed: {
    state: 'ingestFailed',
    label: 'Ingest failed',
    explanation: 'The latest ingest failed and no usable ingest supersedes it.',
    tone: 'error',
    actionCategory: 'retryIngest',
  },
  queuedForIngest: {
    state: 'queuedForIngest',
    label: 'Queued for ingest',
    explanation: 'The upload is waiting for asynchronous ingest.',
    tone: 'info',
    actionCategory: 'trackIngest',
  },
  needsCaseAssignment: {
    state: 'needsCaseAssignment',
    label: 'Needs case assignment',
    explanation: 'Ingest succeeded, but the slide has not been assigned to a case.',
    tone: 'warning',
    actionCategory: 'organizeSlides',
  },
  assignedToCase: {
    state: 'assignedToCase',
    label: 'Assigned to case',
    explanation: 'Ingest succeeded and the slide is assigned to a case.',
    tone: 'success',
    actionCategory: 'openAssignedCase',
  },
};

export const getSlideIntakeLifecycle = (evidence: SlideIntakeEvidence): SlideIntakeLifecyclePresentation => {
  if (evidence.requestState === 'loading') return lifecyclePresentations.loading;
  if (evidence.requestState === 'error') return lifecyclePresentations.requestError;
  if (evidence.isHidden) return lifecyclePresentations.hidden;
  if (evidence.fileKind === 'unsupported') return lifecyclePresentations.unsupportedFile;
  if (evidence.fileKind === 'manifest') return lifecyclePresentations.manifestFile;
  if (evidence.hasContradictoryEvidence) return lifecyclePresentations.needsAttention;
  if (evidence.requestState === 'stale') return lifecyclePresentations.stale;
  if (evidence.hasRejectedIngest && !evidence.hasActiveIngest) return lifecyclePresentations.rejectedIngest;
  if (evidence.hasActiveIngest) return lifecyclePresentations.ingesting;
  if (evidence.hasFailedIngest && !evidence.hasUsableSuccessfulIngest) return lifecyclePresentations.ingestFailed;
  if (evidence.hasUsableSuccessfulIngest && evidence.hasCaseAssignment) {
    return lifecyclePresentations.assignedToCase;
  }
  if (evidence.hasUsableSuccessfulIngest) return lifecyclePresentations.needsCaseAssignment;

  return lifecyclePresentations.queuedForIngest;
};
